import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import MultiNodeBarChartContainer from "@features/results/Result/MultiNodeBarChartContainer"
import { GraphNodeEdgeData } from "@store/services/converter/types"
import React, { useMemo, useRef } from "react"
import { RunDto } from "@custom-types/modelRun"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import {
  ChartGroupLayout,
  DashboardBarChartDto,
  DashboardChartAggregateDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { StackedBarChartOptions } from "@features/results/Result/MultiNodeBarChartContainer/ChartOptionsDropdown"
import {
  DashboardConfig,
  useDashboardConfig,
} from "@features/results/Result/Dashboard/dashboardConfig"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { calculateLagShift } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

export type SelectedNodes = { nodes: string[]; index: number }

const defaultOptions: {
  singleRun: Partial<DashboardConfig>
  multiRun: Partial<DashboardConfig>
} = {
  singleRun: {
    stacked: true,
    showAsPercentage: false,
  },
  multiRun: {
    stacked: true,
    showAsPercentage: false,
  },
}

export const DashboardBarChart = (props: {
  chart: DashboardBarChartDto
  graphData: GraphNodeEdgeData
  runs: RunDto[]
  maintainAspectRatio?: boolean
  layout?: ChartGroupLayout
}) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    selectedNodeIndex: 0,
    hiddenDatasets: [],

    // for comparison it has to be stacked, otherwise read from chart options / chart / defaults
    stacked: props.chart.isCompareResultsChart
      ? defaultOptions.multiRun.stacked
      : props.chart.NodeSelect?.[0]?.Stacked ??
        props.chart.Stacked ??
        defaultOptions.singleRun.stacked,

    // for comparison it has to be false, otherwise read from chart options / chart / defaults
    showAsPercentage: props.chart.isCompareResultsChart
      ? defaultOptions.multiRun.showAsPercentage
      : props.chart.NodeSelect?.[0]?.ShowAsPercentage ??
        props.chart.ShowAsPercentage ??
        defaultOptions.singleRun.showAsPercentage,
  })

  const runKeys = props.runs.map((run) => run.key)
  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []
  const selectedNodesWithIndex: SelectedNodes = {
    nodes: selectedNodes,
    index: cfg.selectedNodeIndex ?? 0,
  }

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (
    option: NonNullable<DashboardBarChartDto["NodeSelect"]>[number],
    index: number,
  ) => {
    updateCfg((draft) => {
      draft.selectedNodes = option.Nodes
      draft.selectedNodeIndex = index

      if (props.chart.isCompareResultsChart) {
        draft.stacked = defaultOptions.multiRun.stacked
        draft.showAsPercentage = defaultOptions.multiRun.showAsPercentage
      } else {
        draft.stacked = defaultOptions.singleRun.stacked
        draft.showAsPercentage = defaultOptions.singleRun.showAsPercentage

        if ("Stacked" in option) draft.stacked = option.Stacked
        if ("ShowAsPercentage" in option) draft.showAsPercentage = option.ShowAsPercentage
      }
    })
  }

  const handleShowAsTableToggle = (newValue: boolean) => {
    updateCfg((draft) => {
      draft.showAsTable = newValue
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys,
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: selectedNodes.length === 0 || !isVisible,
    },
  )
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const runResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )

  const nodesData = Object.values(props.graphData.nodeMap).filter((n) =>
    selectedNodes.includes(n.display.label),
  )

  const aggregate = props.chart.Aggregate ?? DashboardChartAggregateDto.median
  const isMean = aggregate === DashboardChartAggregateDto.mean
  const showStDev = isMean && props.chart.ShowStandardDeviations

  const chartOptions: StackedBarChartOptions = {
    sliderUpperValue: 70,
    sliderLowerValue: 30,
    isMean,
    showStDev,
    isStacked: cfg.stacked,
    showAsPercentage: cfg.showAsPercentage,
  }

  const nodesLabels = props.chart.Nodes ?? selectedNodes

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={handleOptionChange}
      onFullScreen={handleFullscreen}
      showAsTable={cfg.showAsTable}
      onShowAsTableToggle={handleShowAsTableToggle}
      runIds={runKeys}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isVisible && (
        <MultiNodeBarChartContainer
          graphData={props.graphData}
          nodesData={nodesData}
          runResults={runResults}
          lag={lagShift}
          isLoading={isFetching}
          runs={props.runs}
          onlyGraph
          chartOptions={chartOptions}
          maintainAspectRatio={false}
          customLabels={nodesLabels}
          legendPosition={props.chart.LegendPosition}
          containerSx={{ height: "100%", overflowY: "auto" }}
          chart={props.chart}
          selectedNodes={selectedNodesWithIndex}
          showAsTable={cfg.showAsTable}
          onShowAsTable={handleShowAsTableToggle}
          showLegend={cfg.showLegend}
          hiddenDatasets={cfg.hiddenDatasets}
          onLegendClick={handleLegendClick}
          title={props.chart.Title + " - " + props.runs.map((run) => run.key).join(" vs ")}
        />
      )}
    </DashboardChartContainer>
  )
}
